import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sunrise, UtensilsCrossed, Sun, Sunset, Moon, Home, Shuffle, Save, Share2, Check, RefreshCcw, Plus, ArrowRight, Trash2, Route, PartyPopper, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ACTIVITIES, SLOTS, useBooking, API } from "../../data";

const SLOT_ICONS = { morning: Sunrise, lunch: UtensilsCrossed, afternoon: Sun, sunset: Sunset, evening: Moon, villa: Home };
const TRAVELERS = ["Family", "Couple", "Friends", "Solo traveller"];
const PACES = ["Relaxed", "Balanced", "Full adventure"];
const DURATIONS = ["Half day", "One full day", "Weekend"];
const LUNCH_FALLBACK = { id: "lunch-centre", title: "Lunch near Centre Street", desc: "Island cafés and shrimp baskets on the historic strip.", img: "/images/island/centre-street.jpg", travel: 6, tags: ["Family"] };

const matchesTraveler = (a, traveler) =>
    traveler === "Solo traveller" ? true :
    traveler === "Family" ? a.tags.includes("Family") :
    traveler === "Friends" ? (a.tags.includes("Adventure") || a.tags.includes("Family") || a.tags.includes("Couples")) :
    (a.tags.includes("Couples") || a.tags.includes("Adventure"));

export function buildItinerary({ traveler, interests, pace, saved, seedOffset = 0 }) {
    const pool = interests.length ? ACTIVITIES.filter((a) => interests.includes(a.cat)) : ACTIVITIES;
    const savedPool = saved.filter((a) => !interests.length || interests.includes(a.cat));
    const relaxed = pace === "Relaxed";
    const pickFor = (slotId, used) => {
        if (slotId === "lunch") {
            return pool.find((a) => a.cat === "food" && !used.has(a.id)) || LUNCH_FALLBACK;
        }
        let cands = pool.filter((a) => a.slot === slotId && !used.has(a.id));
        if (!cands.length) return null;
        const matched = cands.filter((a) => matchesTraveler(a, traveler));
        const source = matched.length ? matched : cands;
        return source[seedOffset % source.length];
    };
    const slotsToFill = relaxed ? ["morning", "lunch", "villa", "sunset"] : ["morning", "lunch", "afternoon", "sunset", "evening", "villa"];
    const used = new Set();
    const slots = [];
    // Saved activities get first claim on their slot
    for (const a of savedPool) {
        if (slotsToFill.includes(a.slot) && !slots.find((s) => s.slot === a.slot)) {
            slots.push({ slot: a.slot, activity: a });
            used.add(a.id);
        }
    }
    for (const slotId of slotsToFill) {
        if (slots.find((s) => s.slot === slotId)) continue;
        const a = pickFor(slotId, used);
        if (a) { slots.push({ slot: slotId, activity: a }); used.add(a.id); }
    }
    const order = ["morning", "lunch", "afternoon", "sunset", "evening", "villa"];
    slots.sort((x, y) => order.indexOf(x.slot) - order.indexOf(y.slot));
    const travel = slots.reduce((t, s) => t + (s.activity.travel || 0), 0);
    return { slots, travel };
}

export default function DayPlanner({ saved, toggle, clearSaved }) {
    const { openBooking } = useBooking();
    const [traveler, setTraveler] = useState(null);
    const [interests, setInterests] = useState([]);
    const [pace, setPace] = useState(null);
    const [duration, setDuration] = useState(null);
    const [plan, setPlan] = useState(null);
    const [celebrate, setCelebrate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedPlanId, setSavedPlanId] = useState(null);
    const [seed, setSeed] = useState(0);

    const ready = traveler && pace && duration;
    const progress = plan ? plan.slots.length / 6 : Math.min(saved.length / 6, 1);

    const toggleInterest = (id) => setInterests((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

    const generate = (seedBump = 0) => {
        const s = seed + seedBump;
        setSeed(s);
        const result = buildItinerary({ traveler: traveler || "Family", interests, pace: pace || "Balanced", saved, seedOffset: s });
        setPlan(result);
        setSavedPlanId(null);
        setCelebrate(true);
        setTimeout(() => setCelebrate(false), 2200);
    };

    const surprise = () => {
        const t = TRAVELERS[Math.floor(Math.random() * TRAVELERS.length)];
        const shuffled = ["beach", "history", "nature", "food", "shopping", "water", "family"].sort(() => Math.random() - 0.5).slice(0, 3);
        const p = PACES[Math.floor(Math.random() * PACES.length)];
        const d = DURATIONS[Math.floor(Math.random() * DURATIONS.length)];
        setTraveler(t); setInterests(shuffled); setPace(p); setDuration(d);
        const result = buildItinerary({ traveler: t, interests: shuffled, pace: p, saved, seedOffset: Math.floor(Math.random() * 20) });
        setPlan(result);
        setSavedPlanId(null);
        setCelebrate(true);
        setTimeout(() => setCelebrate(false), 2200);
    };

    const replaceSlot = (slotId) => {
        if (!plan) return;
        const usedIds = new Set(plan.slots.map((s) => s.activity.id));
        const current = plan.slots.find((s) => s.slot === slotId)?.activity;
        const pool = (interests.length ? ACTIVITIES.filter((a) => interests.includes(a.cat)) : ACTIVITIES)
            .filter((a) => a.slot === slotId && !usedIds.has(a.id));
        const next = pool[0] || (slotId === "lunch" ? LUNCH_FALLBACK : null);
        if (!next || next.id === current?.id) { toast("No alternative for this slot right now"); return; }
        const slots = plan.slots.map((s) => s.slot === slotId ? { slot: slotId, activity: next } : s);
        setPlan({ slots, travel: slots.reduce((t, s) => t + (s.activity.travel || 0), 0) });
        setSavedPlanId(null);
    };

    const savePlan = async () => {
        if (!plan) return;
        setSaving(true);
        try {
            const { data } = await axios.post(`${API}/plans`, {
                traveler, interests, pace, duration,
                days: plan.slots.map((s) => ({ slot: s.slot, activity: s.activity.title })),
                travel_minutes: plan.travel,
            });
            setSavedPlanId(data.id);
            toast.success("Island day saved!");
        } catch { toast.error("Could not save — try again"); }
        finally { setSaving(false); }
    };

    const sharePlan = async () => {
        if (!plan) return;
        const text = `My ${traveler || ""} Amelia Island Day:\n` + plan.slots.map((s) => `• ${SLOTS.find((x) => x.id === s.slot)?.label}: ${s.activity.title}`).join("\n") + `\nTravel time ≈ ${plan.travel} min — planned at Fernandina Shores #6322`;
        try {
            if (navigator.share) await navigator.share({ title: "My Island Day", text });
            else { await navigator.clipboard.writeText(text); toast.success("Plan copied — paste it to your group chat"); }
        } catch { /* user cancelled */ }
    };

    const pill = (active) => `rounded-full px-5 py-2.5 text-sm font-semibold border transition-all ${active ? "bg-[#0F2837] text-white border-[#0F2837]" : "border-[#E5DCCB] bg-white text-[#5C6A78] hover:border-[#C49A45]"}`;

    return (
        <div data-testid="day-planner-section">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#C49A45] font-semibold">Step three</p>
                    <h3 className="font-serif-d font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F2837] mt-2">
                        Build my <em className="text-[#2D6A76]">island day.</em>
                    </h3>
                </div>
                <button onClick={surprise} className="btn-amber-lux inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold" data-testid="surprise-me-button">
                    <Shuffle size={15} /> Surprise me
                </button>
            </div>

            {/* progress */}
            <div className="mb-8 rounded-2xl border border-[#E5DCCB] bg-white p-5" data-testid="vacation-progress">
                <div className="flex items-center justify-between text-xs text-[#5C6A78] mb-2">
                    <span className="font-semibold uppercase tracking-[0.2em]">Your vacation is taking shape</span>
                    <span data-testid="progress-label">{plan ? `${plan.slots.length} of 6 moments planned` : `${saved.length} saved ${saved.length === 1 ? "experience" : "experiences"}`}</span>
                </div>
                <div className="h-2.5 rounded-full bg-[#F3EDE2] overflow-hidden">
                    <motion.div animate={{ width: `${Math.max(progress * 100, 3)}%` }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full rounded-full bg-gradient-to-r from-[#2D6A76] to-[#C49A45]" data-testid="progress-bar" />
                </div>
            </div>

            {/* saved tray */}
            {saved.length > 0 && (
                <div className="mb-10" data-testid="saved-tray">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs uppercase tracking-[0.25em] text-[#5C6A78] font-semibold">Saved experiences · {saved.length}</p>
                        <button onClick={clearSaved} className="text-xs text-[#5C6A78] hover:text-red-500 flex items-center gap-1" data-testid="clear-saved-button"><Trash2 size={12} /> Clear all</button>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                        <AnimatePresence>
                            {saved.map((a) => (
                                <motion.div key={a.id} layout initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, scale: 0.85 }}
                                    className="relative shrink-0 w-36 rounded-2xl overflow-hidden border border-[#E5DCCB] bg-white" data-testid={`saved-chip-${a.id}`}>
                                    <img src={a.img} alt="" className="w-full h-20 object-cover" />
                                    <p className="text-[11px] font-semibold text-[#0F2837] px-2.5 py-2 truncate">{a.title}</p>
                                    <button onClick={() => toggle(a)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0F2837]/80 text-white text-xs flex items-center justify-center" data-testid={`remove-saved-${a.id}`}>×</button>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            )}

            {/* questions */}
            <div className="grid lg:grid-cols-2 gap-x-12 gap-y-7 mb-10" data-testid="planner-questions">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#5C6A78] font-semibold mb-3">Who is travelling?</p>
                    <div className="flex flex-wrap gap-2">{TRAVELERS.map((t) => <button key={t} onClick={() => setTraveler(t)} className={pill(traveler === t)} data-testid={`traveler-${t.toLowerCase().replace(" ", "-")}`}>{t}</button>)}</div>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#5C6A78] font-semibold mb-3">What pace do you want?</p>
                    <div className="flex flex-wrap gap-2">{PACES.map((p) => <button key={p} onClick={() => setPace(p)} className={pill(pace === p)} data-testid={`pace-${p.toLowerCase().replace(" ", "-")}`}>{p}</button>)}</div>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#5C6A78] font-semibold mb-3">What interests you? <span className="normal-case font-normal">(pick any)</span></p>
                    <div className="flex flex-wrap gap-2">
                        {[["beach", "Beach"], ["history", "History"], ["nature", "Nature"], ["food", "Food"], ["shopping", "Shopping"], ["water", "Water adventure"], ["family", "Family fun"]].map(([id, label]) => (
                            <button key={id} onClick={() => toggleInterest(id)} className={pill(interests.includes(id))} data-testid={`interest-${id}`}>{label}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#5C6A78] font-semibold mb-3">How much time do you have?</p>
                    <div className="flex flex-wrap gap-2">{DURATIONS.map((d) => <button key={d} onClick={() => setDuration(d)} className={pill(duration === d)} data-testid={`duration-${d.toLowerCase().replace(/ /g, "-")}`}>{d}</button>)}</div>
                </div>
            </div>

            <button onClick={() => generate(1)} disabled={!ready}
                className="btn-primary-lux w-full sm:w-auto rounded-full px-10 py-4.5 py-4 text-sm font-bold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="build-my-day-button">
                <SparkleIcon /> Build my island day →
            </button>
            {!ready && <p className="mt-2 text-xs text-[#5C6A78]">Answer who's travelling, your pace and your time to generate your day.</p>}

            {/* itinerary */}
            <AnimatePresence>
                {plan && (
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }}
                        className="mt-12 relative rounded-[2rem] bg-[#0F2837] p-7 lg:p-12 overflow-hidden grain" data-testid="itinerary-result">
                        {celebrate && (
                            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                                className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none" data-testid="celebration">
                                <div className="flex items-center gap-3 rounded-full bg-[#C49A45] text-white px-8 py-4 shadow-2xl">
                                    <PartyPopper size={22} />
                                    <span className="font-serif-d text-2xl">Your island day is ready!</span>
                                </div>
                            </motion.div>
                        )}
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C49A45] font-semibold">Your {traveler?.toLowerCase()} island day · {duration?.toLowerCase()}</p>
                                <h4 className="font-serif-d font-light text-3xl lg:text-4xl text-white mt-2" data-testid="itinerary-title">
                                    {plan.slots[0]?.activity.title} <span className="text-[#C49A45]">→</span> {plan.slots[plan.slots.length - 1]?.activity.title}
                                </h4>
                            </div>
                            <div className="rounded-2xl bg-white/10 backdrop-blur px-5 py-3 flex items-center gap-2 text-white text-sm" data-testid="travel-time">
                                <Route size={16} className="text-[#C49A45]" /> ≈ {plan.travel} min total travel
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="itinerary-timeline">
                            {plan.slots.map((s, i) => {
                                const Icon = SLOT_ICONS[s.slot] || Sun;
                                const label = SLOTS.find((x) => x.id === s.slot)?.label || s.slot;
                                return (
                                    <motion.div key={s.slot} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1, duration: 0.5 }}
                                        className="rounded-3xl bg-white/[0.07] border border-white/10 overflow-hidden backdrop-blur group" data-testid={`itinerary-slot-${s.slot}`}>
                                        <div className="relative h-32 overflow-hidden">
                                            <img src={s.activity.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                            <span className="absolute top-3 left-3 rounded-full bg-[#0F2837]/85 text-[#C49A45] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 flex items-center gap-1.5">
                                                <Icon size={11} /> {label}
                                            </span>
                                            <button onClick={() => replaceSlot(s.slot)} title="Swap this activity"
                                                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-[#C49A45] transition" data-testid={`replace-slot-${s.slot}`}>
                                                <RefreshCcw size={13} />
                                            </button>
                                        </div>
                                        <div className="p-5">
                                            <p className="font-serif-d text-xl text-white leading-snug">{s.activity.title}</p>
                                            <p className="text-white/60 text-xs mt-1.5 leading-relaxed line-clamp-2">{s.activity.desc}</p>
                                            <p className="text-[#C49A45]/90 text-[11px] mt-2 font-semibold uppercase tracking-wider">{s.activity.time}{s.activity.best ? ` · ${s.activity.best}` : ""}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                            <div className="rounded-3xl border border-dashed border-white/20 flex flex-col items-center justify-center p-6 text-center min-h-[200px]">
                                <p className="text-white/50 text-sm">Want another day?<br />Keep the same villa, change the mood.</p>
                                <button onClick={() => generate(1)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#C49A45] text-[#C49A45] px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#C49A45] hover:text-white transition" data-testid="add-another-day-button">
                                    <Plus size={13} /> Build another day
                                </button>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3" data-testid="itinerary-actions">
                            <button onClick={savePlan} disabled={saving || !!savedPlanId} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-6 py-3 text-sm font-semibold hover:bg-white/20 transition disabled:opacity-60" data-testid="save-plan-button">
                                {saving ? <Loader2 size={15} className="animate-spin" /> : savedPlanId ? <Check size={15} className="text-[#C49A45]" /> : <Save size={15} />}
                                {savedPlanId ? "Saved" : "Save the plan"}
                            </button>
                            <button onClick={sharePlan} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-6 py-3 text-sm font-semibold hover:bg-white/20 transition" data-testid="share-plan-button">
                                <Share2 size={15} /> Share with your group
                            </button>
                            <button onClick={() => openBooking()} className="btn-amber-lux inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold" data-testid="continue-to-booking-button">
                                Continue to villa booking <ArrowRight size={15} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function SparkleIcon() {
    return <PartyPopper size={16} />;
}
